import { useState, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { GroupMembers } from '@/features/admin/components/GroupMembers'
import { adminApi } from '@/features/admin/api/admin'
import { useFileBrowserStore } from '../stores/fileBrowserStore'
import { api, getErrorMessage } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'

interface ManageGroupDialogProps {
  groupId: number
  groupName: string
  onClose: () => void
}

export function ManageGroupDialog({ groupId, groupName, onClose }: ManageGroupDialogProps) {
  const storeEnabled = useFileBrowserStore(s => s.currentRegistryEnabled)
  const [registryEnabled, setRegistryEnabled] = useState(storeEnabled)
  const [require2FA, setRequire2FA] = useState(false)
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api.listGroups().then(groups => {
      const g = groups.find(g => g.id === groupId)
      if (g) { setRegistryEnabled(g.registry_enabled); setDescription(g.description); setRequire2FA(g.require_2fa) }
    }).catch(err => toast.error(getErrorMessage(err)))
  }, [groupId])

  const setRegistry = async (enabled: boolean) => {
    setSaving(true)
    setSaved(false)
    try {
      await adminApi.updateGroup(groupId, { name: groupName, description, registry_enabled: enabled })
      setRegistryEnabled(enabled)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) { toast.error(getErrorMessage(err)) }
    setSaving(false)
  }

  const set2FA = async (enabled: boolean) => {
    setSaving(true)
    setSaved(false)
    try {
      await adminApi.updateGroup(groupId, { name: groupName, description, require_2fa: enabled })
      setRequire2FA(enabled)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) { toast.error(getErrorMessage(err)) }
    setSaving(false)
  }

  return (
    <Modal title={`Manage Group: ${groupName}`} onClose={onClose} wide>
      <div className="setting-row" data-testid="registry-toggle-section">
        <label className="setting-label">Case Registry</label>
        <button
          className={`setting-toggle ${registryEnabled ? 'on' : 'off'}`}
          disabled={saving}
          data-testid="registry-enabled-toggle"
          onClick={() => setRegistry(!registryEnabled)}
          title={registryEnabled ? 'Click to disable registry' : 'Click to enable registry'}
        >
          <span className="toggle-track">
            <span className="toggle-thumb" />
          </span>
          <span className="toggle-label">{saving ? 'Saving…' : registryEnabled ? 'ON' : 'OFF'}</span>
        </button>
        {saved && <span className="setting-saved" data-testid="registry-saved">Saved ✓</span>}
      </div>
      <div className="setting-row" data-testid="require-2fa-toggle-section">
        <label className="setting-label">Require 2FA for Downloads</label>
        <button
          className={`setting-toggle ${require2FA ? 'on' : 'off'}`}
          disabled={saving}
          data-testid="require-2fa-toggle"
          onClick={() => set2FA(!require2FA)}
          title={require2FA ? 'Click to disable 2FA requirement' : 'Click to require 2FA for downloads'}
        >
          <span className="toggle-track">
            <span className="toggle-thumb" />
          </span>
          <span className="toggle-label">{saving ? 'Saving…' : require2FA ? 'ON' : 'OFF'}</span>
        </button>
        {saved && <span className="setting-saved">Saved ✓</span>}
      </div>
      <GroupMembers groupId={groupId} groupName={groupName} />
    </Modal>
  )
}
